import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { StatusBadge } from './StatusBadge';
import { User, ArrowDownLeft, ArrowUpRight, RefreshCw, FileText } from 'lucide-react';
import { matchTxApi, sessionTxApi, clientsApi } from '../../lib/api';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export function ClientTransactionsDialog({ 
  open, 
  onClose, 
  clientId, 
  clientName,
  matchId = null,  // Optional: filter by specific match
  transactions = null, // Optional: pass transactions directly instead of fetching
  commissionEntries = null, // Optional: commission entries from trial balance
  sourceTransactions = null, // Optional: source transactions for reference entries
  isReferenceEntry = false // Optional: whether this is a reference commission entry
}) {
  const [loading, setLoading] = useState(false);
  const [matchTxns, setMatchTxns] = useState([]);
  const [sessionTxns, setSessionTxns] = useState([]);
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    if (open && clientId) {
      fetchClientTransactions();
    }
  }, [open, clientId, matchId]);

  const fetchClientTransactions = async () => {
    if (transactions) {
      // Use passed transactions
      const clientTxns = transactions.filter(t => t.client_id === clientId);
      setMatchTxns(clientTxns);
      setSessionTxns([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch all match transactions
      const allMatchTxns = await matchTxApi.list(matchId);
      const clientMatchTxns = allMatchTxns.filter(t => t.client_id === clientId);
      setMatchTxns(clientMatchTxns);

      // Fetch all session transactions if no specific match
      if (!matchId) {
        const allSessionTxns = await sessionTxApi.list();
        const clientSessionTxns = allSessionTxns.filter(t => t.client_id === clientId);
        setSessionTxns(clientSessionTxns);
      } else {
        // Fetch session transactions for sessions of this match
        const allSessionTxns = await sessionTxApi.list(null, matchId);
        const clientSessionTxns = allSessionTxns.filter(t => t.client_id === clientId);
        setSessionTxns(clientSessionTxns);
      }

      // Fetch client data
      try {
        const clients = await clientsApi.list();
        const client = clients.find(c => c.client_id === clientId);
        setClientData(client);
      } catch (e) {
        console.error('Failed to fetch client data:', e);
      }
    } catch (error) {
      console.error('Failed to fetch client transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const matchTotals = matchTxns.reduce((acc, tx) => {
    acc.amount += tx.amount || 0;
    acc.team_a += tx.team_a_impact || 0;
    acc.team_b += tx.team_b_impact || 0;
    return acc;
  }, { amount: 0, team_a: 0, team_b: 0 });

  const sessionTotals = sessionTxns.reduce((acc, tx) => {
    acc.amount += tx.amount || 0;
    return acc;
  }, { amount: 0 });

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Client Transactions Report`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Client Info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Client: ${clientName || clientId}`, 14, yPos);
    yPos += 6;
    if (clientData) {
      doc.setFontSize(10);
      doc.text(`Nick Name: ${clientData.nick_name || '-'}  |  Match Comm: ${clientData.match_user_commission || 0}%  |  Session Comm: ${clientData.session_user_commission || 0}%`, 14, yPos);
      yPos += 6;
    }
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, yPos);
    yPos += 10;

    // Summary
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Match Transactions: ${matchTxns.length}  |  Session Transactions: ${sessionTxns.length}`, 14, yPos);
    yPos += 5;
    doc.text(`Team A Position: ${matchTotals.team_a.toFixed(2)}  |  Team B Position: ${matchTotals.team_b.toFixed(2)}`, 14, yPos);
    yPos += 10;

    // Match Transactions Table
    if (matchTxns.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Match Transactions (${matchTxns.length})`, 14, yPos);
      yPos += 2;

      doc.autoTable({
        startY: yPos,
        head: [['Time', 'Mode', 'Team', 'Rate', 'Amount', 'Team A', 'Team B']],
        body: matchTxns.map(tx => [
          tx.created_at ? format(new Date(tx.created_at), 'dd/MM HH:mm') : '-',
          tx.mode,
          tx.team || '-',
          tx.rate?.toString() || '-',
          tx.amount?.toFixed(2) || '0.00',
          tx.team_a_impact?.toFixed(2) || '0.00',
          tx.team_b_impact?.toFixed(2) || '0.00'
        ]),
        foot: [['', '', '', 'Total:', matchTotals.amount.toFixed(2), matchTotals.team_a.toFixed(2), matchTotals.team_b.toFixed(2)]],
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        footStyles: { fillColor: [243, 244, 246], textColor: 0, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Session Transactions Table
    if (sessionTxns.length > 0) {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 15;
      }

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Session Transactions (${sessionTxns.length})`, 14, yPos);
      yPos += 2;

      doc.autoTable({
        startY: yPos,
        head: [['Time', 'Session', 'Mode', 'Rate', 'Run', 'Amount']],
        body: sessionTxns.map(tx => [
          tx.created_at ? format(new Date(tx.created_at), 'dd/MM HH:mm') : '-',
          tx.session_name || tx.session_id?.slice(0, 8) || '-',
          tx.mode,
          tx.rate?.toString() || '-',
          tx.run?.toString() || '-',
          tx.amount?.toFixed(2) || '0.00'
        ]),
        foot: [['', '', '', '', 'Total:', sessionTotals.amount.toFixed(2)]],
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [147, 51, 234], textColor: 255 },
        footStyles: { fillColor: [243, 244, 246], textColor: 0, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 }
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      doc.text('YellowGold Pro', 14, doc.internal.pageSize.getHeight() - 10);
    }

    // Save the PDF
    const fileName = `${(clientName || clientId).replace(/[^a-zA-Z0-9]/g, '_')}_transactions_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
    doc.save(fileName);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isReferenceEntry ? 'Reference Commission Details' : 'Transactions'}: {clientName || clientId}
            {isReferenceEntry && (
              <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                P&L Entry
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Source Transactions - Show for reference entries */}
            {isReferenceEntry && sourceTransactions && sourceTransactions.length > 0 && (
              <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <Label className="text-sm font-medium mb-2 block text-purple-700 dark:text-purple-300">
                  Commission Sources (from which clients)
                </Label>
                <div className="space-y-2">
                  {sourceTransactions.map((src, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white dark:bg-purple-950/50 rounded p-2 text-xs">
                      <div>
                        <span className="font-medium">{src.client_name}</span>
                        <span className="text-muted-foreground ml-2">
                          (Book: {src.client_book?.toFixed(2)})
                        </span>
                      </div>
                      <span className="font-mono font-medium text-purple-600 dark:text-purple-400">
                        {src.commission_amount?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-purple-200 dark:border-purple-700 flex justify-between text-sm">
                  <span className="font-medium text-purple-700 dark:text-purple-300">Total Commission:</span>
                  <span className="font-mono font-bold text-purple-700 dark:text-purple-300">
                    {sourceTransactions.reduce((sum, s) => sum + (s.commission_amount || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Client Info */}
            {clientData && !isReferenceEntry && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="bg-muted p-2 rounded">
                  <Label className="text-[10px] text-muted-foreground">Nick Name</Label>
                  <p className="font-medium">{clientData.nick_name || '-'}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-[10px] text-muted-foreground">Match Commission</Label>
                  <p className="font-medium">{clientData.match_user_commission || 0}%</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-[10px] text-muted-foreground">Session Commission</Label>
                  <p className="font-medium">{clientData.session_user_commission || 0}%</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-[10px] text-muted-foreground">Limit</Label>
                  <p className="font-medium">{clientData.limit_unlimited ? 'Unlimited' : (clientData.limit_amount || 0)}</p>
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded">
                <Label className="text-xs text-blue-600">Match Txns</Label>
                <p className="text-xl font-bold">{matchTxns.length}</p>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded">
                <Label className="text-xs text-purple-600">Session Txns</Label>
                <p className="text-xl font-bold">{sessionTxns.length}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded">
                <Label className="text-xs text-emerald-600">Team A Position</Label>
                <p className={`text-xl font-bold ${matchTotals.team_a >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {matchTotals.team_a.toFixed(2)}
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded">
                <Label className="text-xs text-amber-600">Team B Position</Label>
                <p className={`text-xl font-bold ${matchTotals.team_b >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {matchTotals.team_b.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Match Transactions Table */}
            {matchTxns.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <ArrowDownLeft className="h-4 w-4 text-blue-500" />
                  Match Transactions ({matchTxns.length})
                </Label>
                <div className="border rounded overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Time</th>
                        <th className="px-2 py-1 text-left">Mode</th>
                        <th className="px-2 py-1 text-left">Team</th>
                        <th className="px-2 py-1 text-right">Rate</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                        <th className="px-2 py-1 text-right">Team A</th>
                        <th className="px-2 py-1 text-right">Team B</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchTxns.map((tx, idx) => (
                        <tr key={tx.txn_id || idx} className="border-t hover:bg-muted/50">
                          <td className="px-2 py-1">
                            {tx.created_at ? format(new Date(tx.created_at), 'dd/MM HH:mm') : '-'}
                          </td>
                          <td className="px-2 py-1"><StatusBadge status={tx.mode} /></td>
                          <td className="px-2 py-1">{tx.team}</td>
                          <td className="px-2 py-1 text-right font-mono">{tx.rate}</td>
                          <td className="px-2 py-1 text-right font-mono">{tx.amount?.toFixed(2)}</td>
                          <td className={`px-2 py-1 text-right font-mono ${tx.team_a_impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {tx.team_a_impact?.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right font-mono ${tx.team_b_impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {tx.team_b_impact?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted font-medium">
                      <tr>
                        <td colSpan={4} className="px-2 py-1 text-right">Total:</td>
                        <td className="px-2 py-1 text-right font-mono">{matchTotals.amount.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right font-mono ${matchTotals.team_a >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {matchTotals.team_a.toFixed(2)}
                        </td>
                        <td className={`px-2 py-1 text-right font-mono ${matchTotals.team_b >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {matchTotals.team_b.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Session Transactions Table */}
            {sessionTxns.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-purple-500" />
                  Session Transactions ({sessionTxns.length})
                </Label>
                <div className="border rounded overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Time</th>
                        <th className="px-2 py-1 text-left">Session</th>
                        <th className="px-2 py-1 text-left">Mode</th>
                        <th className="px-2 py-1 text-right">Rate</th>
                        <th className="px-2 py-1 text-right">Run</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionTxns.map((tx, idx) => (
                        <tr key={tx.txn_id || idx} className="border-t hover:bg-muted/50">
                          <td className="px-2 py-1">
                            {tx.created_at ? format(new Date(tx.created_at), 'dd/MM HH:mm') : '-'}
                          </td>
                          <td className="px-2 py-1">{tx.session_name || tx.session_id?.slice(0, 8)}</td>
                          <td className="px-2 py-1"><StatusBadge status={tx.mode} /></td>
                          <td className="px-2 py-1 text-right font-mono">{tx.rate}</td>
                          <td className="px-2 py-1 text-right font-mono">{tx.run}</td>
                          <td className="px-2 py-1 text-right font-mono">{tx.amount?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted font-medium">
                      <tr>
                        <td colSpan={5} className="px-2 py-1 text-right">Total:</td>
                        <td className="px-2 py-1 text-right font-mono">{sessionTotals.amount.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Commission Entries Section */}
            {commissionEntries && commissionEntries.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <span className="h-4 w-4 rounded bg-gradient-to-r from-amber-500 to-purple-500"></span>
                  Commission Entries ({commissionEntries.length})
                </Label>
                <div className="border rounded overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Type</th>
                        <th className="px-2 py-1 text-left">Description</th>
                        <th className="px-2 py-1 text-right">Calc. On</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissionEntries.map((entry, idx) => {
                        // Color coding based on entry type
                        const isUserComm = entry.type === 'user_commission';
                        const isRefComm = entry.type === 'ref_commission' || entry.type === 'patti_ref_commission';
                        const isRefReceived = entry.type === 'ref_commission_received' || entry.type === 'patti_ref_commission_received';
                        
                        let rowClass = 'border-t hover:bg-muted/50';
                        let typeClass = 'px-1.5 py-0.5 rounded text-[10px] font-medium';
                        
                        if (isUserComm) {
                          rowClass = 'border-t bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/30';
                          typeClass += ' bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200';
                        } else if (isRefComm) {
                          rowClass = 'border-t bg-purple-50 dark:bg-purple-950/20 hover:bg-purple-100 dark:hover:bg-purple-950/30';
                          typeClass += ' bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200';
                        } else if (isRefReceived) {
                          rowClass = 'border-t bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-950/30';
                          typeClass += ' bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200';
                        }
                        
                        return (
                          <tr key={idx} className={rowClass}>
                            <td className="px-2 py-1">
                              <span className={typeClass}>
                                {isUserComm ? 'User Comm' : isRefComm ? 'Ref Comm' : isRefReceived ? 'Ref Received' : entry.type}
                              </span>
                            </td>
                            <td className="px-2 py-1">
                              {entry.description}
                              {entry.ref_client_name && (
                                <span className="text-purple-600 dark:text-purple-400 ml-1">
                                  → {entry.ref_client_name}
                                </span>
                              )}
                              {entry.from_client_name && (
                                <span className="text-blue-600 dark:text-blue-400 ml-1">
                                  ← from {entry.from_client_name}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-muted-foreground">
                              {entry.calculated_on?.toFixed(2) || '-'}
                            </td>
                            <td className={`px-2 py-1 text-right font-mono font-medium ${entry.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {entry.amount >= 0 ? '+' : ''}{entry.amount?.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-4 mt-2 text-[10px]">
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-800"></span>
                    <span>User Commission</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-purple-200 dark:bg-purple-800"></span>
                    <span>Reference Commission</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-200 dark:bg-blue-800"></span>
                    <span>Commission Received</span>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {matchTxns.length === 0 && sessionTxns.length === 0 && (!commissionEntries || commissionEntries.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                No transactions found for this client
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button 
            variant="outline" 
            onClick={exportToPDF}
            disabled={matchTxns.length === 0 && sessionTxns.length === 0 && (!commissionEntries || commissionEntries.length === 0)}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
